/**
 * Single chip sprite. Uses the same CDN coin images as the Flutter Flame
 * version (imgCoins1.png..imgCoins6.png) when available, falls back to a
 * yellow filled circle if image load fails — matches ChipComponent.render in
 * packages/amgdragontiger_v2/lib/presentation/flame/dragontiger_flame_game.dart:280-295.
 */
import { Assets, Container, Graphics, Sprite, Texture } from 'pixi.js';

const CDN_BASE = 'https://imagebank.b-cdn.net/AMG/Common/imgCoins';

const coinTextureCache = new Map<number, Promise<Texture | null>>();

/** Load (and cache) a coin texture, falling back to null on failure. */
export function loadCoinTexture(chipType: number): Promise<Texture | null> {
  const t = Math.max(1, Math.min(6, Math.floor(chipType)));
  let p = coinTextureCache.get(t);
  if (p) return p;
  p = Assets.load<Texture>(`${CDN_BASE}${t}.png`).catch(() => null);
  coinTextureCache.set(t, p);
  return p;
}

/** Eagerly warm the texture cache so the first chip placement doesn't stall. */
export function preloadCoinTextures(): Promise<unknown> {
  return Promise.all([1, 2, 3, 4, 5, 6].map(loadCoinTexture));
}

export class ChipSprite extends Container {
  readonly value: number;
  readonly chipType: number;

  constructor(value: number, chipIndex: number) {
    super();
    this.value = value;
    // chipType = (index % 6) + 1 — matches dragontiger_flame_game.dart:164
    this.chipType = (chipIndex % 6) + 1;

    // Yellow fallback circle (same look as the Flame fallback in render()).
    // We mount it immediately and replace with the CDN sprite once loaded.
    const fallback = new Graphics();
    const r = 15; // 30×30 px in ref-space, matches Flame size: Vector2(30,30)
    fallback.circle(0, 0, r).fill({ color: 0xffd54f });
    fallback.circle(0, 0, r).stroke({ color: 0xfff8e1, width: 2 });
    this.addChild(fallback);

    void loadCoinTexture(this.chipType).then((tex) => {
      if (!tex) return;
      fallback.destroy();
      const sprite = new Sprite(tex);
      sprite.anchor.set(0.5);
      // The CDN coin PNGs are square ~256×256. Scale to 30 px reference.
      const target = 30;
      sprite.width = target;
      sprite.height = target;
      this.addChildAt(sprite, 0);
    });
  }
}
